import type {
	SRT_Row,
	SRT_TableInstance,
} from '@/shared/components/master-table'
import type {
	FilterRuleModel,
	SettingsTemplateModel,
	SettingsTemplateQueryModel,
} from '@/shared/generated'

import React, { useMemo } from 'react'
import { Link, useParams } from '@tanstack/react-router'
import { isNil, negate } from 'lodash/fp'
import { ClockIcon, PlusIcon, SearchIcon, StarIcon } from 'lucide-react'
import { parseAsInteger, useQueryState } from 'nuqs'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import {
	SettingsTemplateActions,
	useSettingsTemplatesQuery,
	useUpdateSettingsTemplateMutation,
} from '@/entities/settings-template'
import { UsernameById } from '@/entities/user'

import { SettingsTemplateGeneratedCardContent } from '@/widgets/settings-template-description'

import { MasterTable } from '@/shared/components/master-table'
import { SRT_TablePagination } from '@/shared/components/master-table/components/tools/SRT_TablePagination'
import { SRT_TableToolbar } from '@/shared/components/master-table/components/tools/SRT_TableToolbar'
import { Operators } from '@/shared/components/query-builder'
import { Blanket } from '@/shared/components/ui/blanket'
import { Button, buttonVariants } from '@/shared/components/ui/button'
import {
	Card,
	CardContent,
	CardFooter,
	CardHeader,
} from '@/shared/components/ui/card'
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from '@/shared/components/ui/empty'
import { Content, Footer, Header, Layout } from '@/shared/components/ui/layout'
import { LinkButton } from '@/shared/components/ui/link-button'
import { Loader } from '@/shared/components/ui/loader'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { Spinner } from '@/shared/components/ui/spinner'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { SettingsTemplateType } from '@/shared/generated'
import { useLocationQuery } from '@/shared/hooks/useLocationQuery'
import { useModals } from '@/shared/lib/modal-manager'
import { queryClient } from '@/shared/lib/query-client'
import { intlFormat } from '@/shared/tools/dates'

import { useSettingsTemplatesTable } from '../lib/useSettingsTemplatesTable'

export function SettingsTemplatesPage() {
	const { template_id: type } = useParams({
		from: '/_app/p/templates/$template_id/',
	})

	if (!type) {
		return null
	}

	return <TemplatesPage type={type as SettingsTemplateType} />
}

export function TemplatesPage({ type }: { type: SettingsTemplateType }) {
	const { t } = useTranslation()

	const hardFilters = useMemo(
		() => [
			{
				name: 'template_type',
				operation: Operators.IN,
				value: [type],
			} as FilterRuleModel,
		],
		[type]
	)
	const [{ filters }, setQuery] = useLocationQuery()
	const { showModal } = useModals()
	const [page] = useQueryState('page', parseAsInteger.withDefault(1))
	const [limit] = useQueryState('limit', parseAsInteger.withDefault(10))
	const offset = (page - 1) * limit
	const requestBody: SettingsTemplateQueryModel = {
		limit,
		offset,
		filters: [...hardFilters, ...filters].filter(
			negate(isNil)
		) as FilterRuleModel[],
		order_by: [
			{ name: 'is_favorite', direction: 'desc' },
			{ name: 'name', direction: 'asc' },
		],
	}
	const templatesQuery = useSettingsTemplatesQuery(requestBody)
	const data = templatesQuery.data?.data || []
	const isLoading = templatesQuery.isLoading
	const table = useSettingsTemplatesTable({
		filters,
		setFilters: (filters: FilterRuleModel[]) => setQuery({ filters }),
		pageCount: templatesQuery.data
			? Math.ceil(templatesQuery.data?.total / limit)
			: 0,
		data,
		isLoading,
	})

	return (
		<Layout>
			<Header className='my-3 flex items-center justify-between px-4'>
				<div className='mr-auto flex items-center space-x-2'>
					<SRT_TableToolbar table={table} />
				</div>
				<Tooltip>
					<TooltipTrigger asChild>
						{type === SettingsTemplateType.Scenario ? (
							<Link
								to='/p/templates/editor'
								data-testid='create-settings-template-trigger'
								className={buttonVariants({
									variant: 'primary',
									size: 'icon',
								})}
							>
								<PlusIcon />
							</Link>
						) : (
							<Button
								data-testid='create-settings-template-trigger'
								variant='primary'
								size='icon'
								onClick={() =>
									showModal('SETTINGS_TEMPLATE_MODAL', {
										template_type: type,
									})
								}
							>
								<PlusIcon />
							</Button>
						)}
					</TooltipTrigger>
					<TooltipContent>{t('general_create')}</TooltipContent>
				</Tooltip>
			</Header>
			{[SettingsTemplateType.Scenario].includes(type) ? (
				<Content className='overflow-y-auto'>
					<Blanket
						className='absolute flex flex-col items-center justify-center space-y-5 rounded-lg bg-transparent'
						isTinted={isLoading}
					>
						<div className='z-50 rounded-lg bg-white/50 p-5 shadow backdrop-blur-md fade-in dark:bg-black/50'>
							<Loader />
						</div>
					</Blanket>
					<SettingsTemplatesLayout table={table} />
				</Content>
			) : (
				<Content className='overflow-hidden px-4'>
					<Blanket
						className='absolute flex flex-col items-center justify-center space-y-5 rounded-lg bg-transparent'
						isTinted={isLoading}
					>
						<div className='z-50 rounded-lg bg-white/50 p-5 shadow backdrop-blur-md fade-in dark:bg-black/50'>
							<Loader />
						</div>
					</Blanket>
					<div className='border-base flex h-full flex-1 flex-col overflow-hidden rounded-lg border border-solid'>
						<MasterTable table={table} />
					</div>
				</Content>
			)}
			<Footer className='px-4 py-2'>
				<SRT_TablePagination table={table} />
			</Footer>
		</Layout>
	)
}

function SettingsTemplatesLayout({
	table,
}: {
	table: SRT_TableInstance<SettingsTemplateModel>
}) {
	const { t } = useTranslation()

	const { getState } = table
	const { isLoading } = getState()
	if (isLoading) {
		return (
			<div className='grid h-fit w-full grid-cols-3 gap-4 overflow-hidden px-4 pb-4'>
				<Skeleton className='h-32 w-full' />
				<Skeleton className='h-32 w-full' />
				<Skeleton className='h-32 w-full' />
			</div>
		)
	}
	const data = table.getRowModel().rows
	if (!data.length) {
		return (
			<Empty data-testid='no-data'>
				<EmptyHeader>
					<EmptyMedia variant='icon'>
						<SearchIcon className='text-muted-foreground' />
					</EmptyMedia>
					<EmptyTitle>
						{t('general_no_templates', {
							defaultValue: 'There is no templates',
						})}
					</EmptyTitle>
					<EmptyDescription>
						{t('general_no_templates_description', {
							defaultValue:
								'There are no templates available for the selected language or search.',
						})}
					</EmptyDescription>
				</EmptyHeader>
			</Empty>
		)
	}

	return (
		<div className='grid h-fit w-full grid-cols-3 gap-4 overflow-hidden px-4 pb-4'>
			{data.map((row: SRT_Row<SettingsTemplateModel>, i) => {
				const template = row.original

				return (
					<SettingsTemplateCard key={`${template.id}`} template={template} />
				)
			})}
		</div>
	)
}
function SettingsTemplateCard({
	template,
}: {
	template: SettingsTemplateModel
}) {
	const { t } = useTranslation()

	const updateTemplateMutation = useUpdateSettingsTemplateMutation(
		template.id,
		{
			onSuccess: (result) => {
				queryClient.invalidateQueries({
					queryKey: ['settings-templates'],
				})
				queryClient.setQueryData(
					['settings-templates'],
					(outdated?: SettingsTemplateModel[]) =>
						outdated?.map((item) => (template.id === item.id ? result : item))
				)
				toast.success(
					t('toast.entity.updated', { entity: t('entity.template') }),
					{
						duration: 5000,
						id: 'settigs-template',
					}
				)
			},
		}
	)
	const handleToggleFavorite = async () => {
		const data = {
			is_favorite: !template.is_favorite,
		}
		await updateTemplateMutation.mutateAsync(data)
	}

	return (
		<Card
			className='min-h-36 border shadow-[0px_4px_8px_0px_rgba(20,42,69,0.10)]'
			data-testid='settings-template-card'
		>
			<CardHeader>
				<div className='flex items-center justify-between'>
					<LinkButton asChild variant='brand'>
						<Link
							to='/p/templates/editor/$template_id'
							params={{ template_id: template.id }}
							className='block max-w-64 overflow-hidden text-nowrap text-ellipsis'
						>
							{template.name}
						</Link>
					</LinkButton>
					<div>
						<Button
							variant='ghost'
							size='icon-sm'
							disabled={updateTemplateMutation.isPending}
							onClick={handleToggleFavorite}
						>
							{updateTemplateMutation.isPending ? (
								<Spinner className='opacity-50' />
							) : (
								<StarIcon
									className={
										template.is_favorite ? 'fill-warning text-warning' : ''
									}
								/>
							)}
						</Button>
						<SettingsTemplateActions template={template} />
					</div>
				</div>
			</CardHeader>
			<CardContent className='m-0 px-4 pb-2 text-xl text-secondary-foreground'>
				<SettingsTemplateGeneratedCardContent template={template} />
			</CardContent>
			<CardFooter className='gap-2 px-4 pb-4'>
				<span className='flex items-center gap-1 text-xs text-muted-foreground'>
					<ClockIcon className='size-3 text-muted-foreground' />{' '}
					{`${t('updated')}: ${template.updated_at ? intlFormat(new Date(template.updated_at + 'Z')) : '-'} `}{' '}
				</span>
				<div className='flex items-center text-xs text-muted-foreground'>
					<span className='mr-1'>{`${t('by')} `}</span>
					{template.updated_by ? (
						<UsernameById userId={template.updated_by} />
					) : (
						<>{t('app.unknown', { defaultValue: 'Unknown' })}</>
					)}
				</div>
			</CardFooter>
		</Card>
	)
}
